/**
 * components/NotificationBell.js — Толгойд харагдах мэдэгдлийн хонх.
 *
 * Үндсэн зорилго:
 *  • 30 секунд тутамд /api/notify/inbox-руу polling
 *  • Уншаагүй мэдэгдлийн тоог хонхны badge-аар харуулна
 *  • Дарахад dropdown нээгдэж сүүлийн 10 мэдэгдлийг харуулна
 *  • Мэдэгдэл дээр дарвал "уншсан" гэж тэмдэглэж detail-руу шилжинэ
 *
 * Хэрэглэх:
 *   import NotificationBell from "src/components/NotificationBell";
 *   <NotificationBell />
 *
 * Header / Layout-ын дотор нэг л дуудаж тавихад л болно.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Popover, Empty, Tag, Spin } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { API } from "src/api/request";
import { useUserContext } from "src/contexts/userContext";
/* ⚠ ШИЛЖИХ ДҮРЭМ нь components/notifyTarget.js-д (2026-10-28).
   Бүртгэлийн хуудасны `NotificationPanel` ижил дүрэм хэрэглэдэг —
   энд хуулбарлавал хоёр өөр зан төлөвтэй болж, шинэ төрөл нэмэхэд
   нэг газраас «алга» болно. */
import { notifyRoute } from "src/components/notifyTarget";

const POLL_INTERVAL = 30_000; // 30 секунд

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const failCountRef = useRef(0); // дараалсан алдааны тоо (backoff-д)

  const fetchInbox = useCallback(async () => {
    if (!user?.loggedIn || !user?.tn) return;
    setLoading(true);
    try {
      const res = await API().get("/api/notify/inbox", {
        params: { limit: 10 },
      });
      setItems(res.data?.items || []);
      setUnread(res.data?.unread || 0);
      failCountRef.current = 0; // амжилттай → backoff-ыг тэглэнэ
    } catch (err) {
      // Backend бэлэн биш бол silent — header-аар алдаа харуулахгүй.
      // Дараалсан алдаа тутам polling-ийг удаашруулж console-ийн
      // ERR_CONNECTION_REFUSED спамыг багасгана.
      failCountRef.current += 1;
    } finally {
      setLoading(false);
    }
  }, [user?.loggedIn, user?.tn]);

  // Mount-аар нэг удаа + backoff-той polling.
  //   Backend асаалттай:  30 сек тутам.
  //   Backend унтарсан:    30с → 60с → 120с → ... дээд тал нь 5 мин,
  //                        амжилттай болмогц 30 сек рүү буцна.
  useEffect(() => {
    let cancelled = false;
    const schedule = async () => {
      await fetchInbox();
      if (cancelled) return;
      const delay = Math.min(
        POLL_INTERVAL * 2 ** failCountRef.current,
        5 * 60_000,
      );
      timerRef.current = setTimeout(schedule, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchInbox]);

  const handleClick = async (item) => {
    // Уншсан гэж тэмдэглэх
    if (!item.read_at) {
      try {
        await API().put(`/api/notify/log/${item.id}/read`);
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, read_at: new Date().toISOString() } : it,
          ),
        );
        setUnread((u) => Math.max(0, u - 1));
      } catch {}
    }
    /* Entity-ийн дагуу шилжих — дүрэм нь `notifyTarget.js`-д НЭГ л удаа
       бичигдсэн (hazard · assignment · fix_submitted · fix_verified). */
    const r = notifyRoute(item);
    if (r) navigate(r.to, r.state ? { state: r.state } : undefined);
    setOpen(false);
  };

  const dropdownContent = (
    <div
      style={{
        background: "#fff",
        border: "1px solid #EAE9E3",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        width: 360,
        maxHeight: 480,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid #F1F2F4",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700 }}>
          🔔 Мэдэгдэл
          {unread > 0 && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              {unread} шинэ
            </Tag>
          )}
        </span>
        <span
          style={{ fontSize: 11, color: "#9A988F", cursor: "pointer" }}
          onClick={() => {
            navigate("/admin/notifications");
            setOpen(false);
          }}
        >
          Бүгдийг харах →
        </span>
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {loading && items.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center" }}>
            <Spin size="small" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: "20px 14px" }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ fontSize: 12, color: "#9A988F" }}>
                  Мэдэгдэл алга
                </span>
              }
            />
          </div>
        ) : (
          items.map((it) => {
            const isUnread = !it.read_at;
            return (
              <div
                key={it.id}
                onClick={() => handleClick(it)}
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid #F1F2F4",
                  cursor: "pointer",
                  background: isUnread ? "#EFF6FF" : "#fff",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#F8F9FB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = isUnread
                    ? "#EFF6FF"
                    : "#fff")
                }
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: isUnread ? 700 : 500,
                    color: "#2C2C2A",
                    marginBottom: 2,
                  }}
                >
                  {isUnread && (
                    <span
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#DC2626",
                        marginRight: 6,
                      }}
                    />
                  )}
                  {it.title || "(тогтоогүй)"}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                    lineHeight: 1.4,
                    marginLeft: isUnread ? 12 : 0,
                  }}
                >
                  {it.body}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4,
                    fontSize: 10,
                    color: "#9A988F",
                    marginLeft: isUnread ? 12 : 0,
                  }}
                >
                  <span>
                    {it.channel === "push" ? "📱 Push" : "📧 Email"}
                    {it.entity_ref && ` · ${it.entity_ref}`}
                  </span>
                  <span>{formatTime(it.sent_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  if (!user?.loggedIn) return null;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={dropdownContent}
      overlayInnerStyle={{ padding: 0 }}
    >
      <span
        style={{
          cursor: "pointer",
          padding: "6px 10px",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <Badge count={unread} size="small" offset={[-2, 2]}>
          <BellOutlined style={{ fontSize: 18, color: "#3A4153" }} />
        </Badge>
      </span>
    </Popover>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "одоо";
  if (diffMin < 60) return `${diffMin} мин`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} цаг`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} өдөр`;
  return d.toLocaleDateString("mn-MN", { month: "short", day: "numeric" });
}
